import { hashKey, type QueryClient, type QueryKey } from '@tanstack/react-query';

import { _bumpPerf } from '../../devtool/perf-counters.js';
import type { GetPositionsRow } from '../../generated/openapi-types.gen.js';
import { hashDiagnosticEntity } from '../../tx-engine/diagnostics/lifecycle.js';
import { emit } from '../events.js';
import type { NormalizedEntityWriteMeta } from './normalized-entity-write.js';

export const CONFIRMED_POSITION_TRANSITION_WINDOW_MS = 10_000;

const MAX_PENDING_WARNINGS = 3;
const POSITION_ENTITY_PREFIX = 'positions';
const CANONICAL_UNSIGNED_INTEGER = /^(0|[1-9]\d*)$/;

export const CONFIRMED_POSITION_ACCOUNTING_FIELDS: ReadonlySet<string> = new Set([
  'avgEntryPriceD9',
  'balance',
  'claimRightsBalanceD6',
  'costBasisD6',
  'isOpen',
  'prizeClaimableD6',
  'prizeClaimedD6',
  'prizeState',
  'realizedCostBasisD6',
  'realizedCostBasisGrossD6',
  'realizedCostBasisNetD6',
  'realizedPnlD6',
  'realizedPnlGrossD6',
  'realizedPnlNetD6',
  'remainingCostBasisGrossD6',
  'remainingCostBasisNetD6',
  'resultState',
  'tradeCount',
  'unrealizedPnlD6',
]);

export interface ConfirmedPositionTransitionLogger {
  readonly warn: (message: string, context: Record<string, unknown>) => void;
}

export interface ArmConfirmedPositionTransitionInput {
  readonly row: GetPositionsRow;
  readonly direction: 'buy' | 'sell' | 'claim';
  readonly network: string;
  readonly confirmedAtMs: number;
  readonly indexQueryKeys: readonly QueryKey[];
  readonly logger?: ConfirmedPositionTransitionLogger;
  readonly recoveryWindowMs?: number;
}

export interface ConfirmedPositionWriteDecision {
  readonly status: 'allow' | 'pending';
}

interface ConfirmedPositionTransition {
  readonly positionId: string;
  readonly positionHash: string;
  readonly marketHash: string;
  readonly direction: 'buy' | 'sell' | 'claim';
  readonly network: string;
  readonly confirmedAtMs: number;
  readonly expiresAtMs: number;
  readonly expectedBalance: bigint;
  readonly expectedClaimRightsBalanceD6: bigint;
  readonly expectedPrizeClaimableD6: bigint;
  readonly expectedPrizeState: GetPositionsRow['prizeState'];
  readonly expectedIsOpen: boolean;
  readonly expectedState: string;
  readonly indexMembership: ReadonlyMap<string, boolean>;
  readonly logger?: ConfirmedPositionTransitionLogger;
  entityAcknowledged: boolean;
  readonly acknowledgedIndexes: Set<string>;
  attempts: number;
  warnings: number;
  lastObservedAtMs: number | null;
  lastObservedState: string;
}

const confirmedTransitions = new WeakMap<QueryClient, Map<string, ConfirmedPositionTransition>>();

export function armConfirmedPositionTransition(
  queryClient: QueryClient,
  input: ArmConfirmedPositionTransitionInput,
): void {
  if (!Number.isSafeInteger(input.confirmedAtMs) || input.confirmedAtMs < 0) {
    throw new Error('Confirmed position transition requires a valid confirmation time');
  }
  const recoveryWindowMs = input.recoveryWindowMs ?? CONFIRMED_POSITION_TRANSITION_WINDOW_MS;
  if (!Number.isSafeInteger(recoveryWindowMs) || recoveryWindowMs <= 0) {
    throw new Error('Confirmed position transition requires a positive recovery window');
  }
  const expectedBalance = parseUnsigned(input.row.balance);
  const expectedClaimRightsBalanceD6 = parseUnsigned(input.row.claimRightsBalanceD6);
  const expectedPrizeClaimableD6 = parseUnsigned(input.row.prizeClaimableD6);
  if (
    input.row.positionId.length === 0 ||
    expectedBalance === null ||
    expectedClaimRightsBalanceD6 === null ||
    expectedPrizeClaimableD6 === null
  ) {
    throw new Error('Confirmed position transition requires a canonical position');
  }

  const indexMembership = new Map<string, boolean>();
  for (const queryKey of input.indexQueryKeys) {
    indexMembership.set(hashKey(queryKey), expectedIndexMembership(queryKey, input.row));
  }
  const transition: ConfirmedPositionTransition = {
    positionId: input.row.positionId,
    positionHash: hashDiagnosticEntity(input.row.positionId),
    marketHash: hashDiagnosticEntity(input.row.market),
    direction: input.direction,
    network: input.network,
    confirmedAtMs: input.confirmedAtMs,
    expiresAtMs: input.confirmedAtMs + recoveryWindowMs,
    expectedBalance,
    expectedClaimRightsBalanceD6,
    expectedPrizeClaimableD6,
    expectedPrizeState: input.row.prizeState,
    expectedIsOpen: input.row.isOpen,
    expectedState: input.row.resultState,
    indexMembership,
    ...(input.logger === undefined ? {} : { logger: input.logger }),
    entityAcknowledged: false,
    acknowledgedIndexes: new Set<string>(),
    attempts: 0,
    warnings: 0,
    lastObservedAtMs: null,
    lastObservedState: 'missing',
  };
  const transitions = transitionMap(queryClient);
  const key = entityHash(input.row.positionId);
  transitions.delete(key);
  transitions.set(key, transition);
}

export function evaluateConfirmedPositionWrite(
  queryClient: QueryClient,
  queryKey: QueryKey,
  next: object,
  meta: NormalizedEntityWriteMeta,
): ConfirmedPositionWriteDecision {
  if (meta.source !== 'rest' || !isPositionEntityKey(queryKey)) return { status: 'allow' };
  const transitions = confirmedTransitions.get(queryClient);
  const transition = transitions?.get(hashKey(queryKey));
  if (transition === undefined) return { status: 'allow' };
  const observed = next as Readonly<Record<string, unknown>>;
  if (acknowledgesTransition(transition, observed)) {
    transition.entityAcknowledged = true;
    clearAcknowledgedTransition(transitions, hashKey(queryKey), transition);
    return { status: 'allow' };
  }
  recordPendingTransition(transition, observed, meta.observedAtMs);
  if (process.env.NODE_ENV !== 'production') {
    _bumpPerf('positionEntity.confirmedTransitionsHeld');
  }
  return { status: 'pending' };
}

export function applyConfirmedPositionIndexFences(
  queryClient: QueryClient,
  entityQueryKey: (id: string) => readonly unknown[],
  feedQueryKey: QueryKey | undefined,
  ids: readonly string[],
  meta: NormalizedEntityWriteMeta | undefined,
): readonly string[] {
  if (
    feedQueryKey === undefined ||
    meta?.source !== 'rest' ||
    entityQueryKey('__position_probe__')[0] !== POSITION_ENTITY_PREFIX
  ) {
    return ids;
  }
  const transitions = confirmedTransitions.get(queryClient);
  if (transitions === undefined || transitions.size === 0) return ids;
  const feedHash = hashKey(feedQueryKey);
  let nextIds = ids;
  for (const transition of transitions.values()) {
    const shouldInclude = transition.indexMembership.get(feedHash);
    if (shouldInclude === undefined) continue;
    const includesPosition = ids.includes(transition.positionId);
    if (includesPosition === shouldInclude) {
      transition.acknowledgedIndexes.add(feedHash);
      clearAcknowledgedTransition(transitions, entityHash(transition.positionId), transition);
      continue;
    }
    if (includesPosition !== shouldInclude) {
      recordPendingTransition(transition, undefined, meta.observedAtMs);
    }
    nextIds = placePositionId(nextIds, transition.positionId, shouldInclude);
  }
  return nextIds;
}

export function hasPendingConfirmedPositionTransitions(queryClient: QueryClient): boolean {
  return (confirmedTransitions.get(queryClient)?.size ?? 0) > 0;
}

export function releaseExpiredConfirmedPositionTransitions(queryClient: QueryClient): number {
  const transitions = confirmedTransitions.get(queryClient);
  if (transitions === undefined) return 0;
  const now = Date.now();
  let released = 0;
  for (const [key, transition] of transitions) {
    if (now < transition.expiresAtMs) continue;
    recordExpiredTransition(transition);
    transitions.delete(key);
    released += 1;
  }
  return released;
}

function transitionMap(queryClient: QueryClient): Map<string, ConfirmedPositionTransition> {
  let transitions = confirmedTransitions.get(queryClient);
  if (transitions === undefined) {
    transitions = new Map<string, ConfirmedPositionTransition>();
    confirmedTransitions.set(queryClient, transitions);
  }
  return transitions;
}

function clearAcknowledgedTransition(
  transitions: Map<string, ConfirmedPositionTransition> | undefined,
  key: string,
  transition: ConfirmedPositionTransition,
): void {
  if (!transition.entityAcknowledged) return;
  if (transition.acknowledgedIndexes.size !== transition.indexMembership.size) return;
  transitions?.delete(key);
}

function entityHash(positionId: string): string {
  return hashKey([POSITION_ENTITY_PREFIX, 'entity', positionId]);
}

function isPositionEntityKey(queryKey: QueryKey): boolean {
  return queryKey[0] === POSITION_ENTITY_PREFIX && queryKey[1] === 'entity';
}

function acknowledgesTransition(
  transition: ConfirmedPositionTransition,
  observed: Readonly<Record<string, unknown>>,
): boolean {
  const observedBalance = parseUnsigned(observed.balance);
  if (observedBalance === null) return false;
  if (observed.isOpen !== transition.expectedIsOpen) return false;
  if (observed.resultState !== transition.expectedState) return false;
  if (transition.direction === 'claim') {
    const observedClaimRightsBalanceD6 = parseUnsigned(observed.claimRightsBalanceD6);
    const observedPrizeClaimableD6 = parseUnsigned(observed.prizeClaimableD6);
    return (
      observedClaimRightsBalanceD6 === transition.expectedClaimRightsBalanceD6 &&
      observedPrizeClaimableD6 === transition.expectedPrizeClaimableD6 &&
      observed.prizeState === transition.expectedPrizeState
    );
  }
  return transition.direction === 'buy'
    ? observedBalance >= transition.expectedBalance
    : observedBalance <= transition.expectedBalance;
}

function expectedIndexMembership(queryKey: QueryKey, row: GetPositionsRow): boolean {
  const params = queryKey[3];
  if (params === null || typeof params !== 'object' || Array.isArray(params)) return true;
  const status = (params as Readonly<Record<string, unknown>>).status;
  if (status === 'open') return row.isOpen;
  if (status === 'closed') return !row.isOpen;
  return true;
}

function placePositionId(
  ids: readonly string[],
  positionId: string,
  shouldInclude: boolean,
): readonly string[] {
  const withoutPosition = ids.filter((id) => id !== positionId);
  if (!shouldInclude) {
    return withoutPosition.length === ids.length ? ids : withoutPosition;
  }
  const existingIndex = ids.indexOf(positionId);
  const insertionIndex = existingIndex === -1 ? 0 : Math.min(existingIndex, withoutPosition.length);
  const next = [
    ...withoutPosition.slice(0, insertionIndex),
    positionId,
    ...withoutPosition.slice(insertionIndex),
  ];
  return next.length === ids.length && next.every((id, index) => id === ids[index]) ? ids : next;
}

function recordPendingTransition(
  transition: ConfirmedPositionTransition,
  observed: Readonly<Record<string, unknown>> | undefined,
  observedAtMs: number,
): void {
  if (transition.lastObservedAtMs === observedAtMs) return;
  transition.lastObservedAtMs = observedAtMs;
  if (observed !== undefined) transition.lastObservedState = observedState(observed);
  transition.attempts += 1;
  if (transition.warnings >= MAX_PENDING_WARNINGS) return;
  transition.warnings += 1;
  const diagnostic = {
    direction: transition.direction,
    expectedState: transition.expectedState,
    observedState: observedState(observed),
    expectedPrizeState: transition.expectedPrizeState,
    observedPrizeState: observed?.prizeState ?? 'missing',
    attempt: transition.attempts,
    elapsedMs: Math.max(0, Date.now() - transition.confirmedAtMs),
    network: transition.network,
    positionHash: transition.positionHash,
    marketHash: transition.marketHash,
  };
  transition.logger?.warn('position.reconcile.confirmed_transition_pending', diagnostic);
  emit('position-entity:diagnostic', { kind: 'confirmed-transition-pending', ...diagnostic });
}

function recordExpiredTransition(
  transition: ConfirmedPositionTransition,
  observed?: Readonly<Record<string, unknown>>,
): void {
  const diagnostic = {
    direction: transition.direction,
    expectedState: transition.expectedState,
    observedState: observed === undefined ? transition.lastObservedState : observedState(observed),
    expectedPrizeState: transition.expectedPrizeState,
    observedPrizeState: observed?.prizeState ?? 'missing',
    attempt: Math.max(1, transition.attempts),
    elapsedMs: Math.max(0, Date.now() - transition.confirmedAtMs),
    network: transition.network,
    positionHash: transition.positionHash,
    marketHash: transition.marketHash,
  };
  transition.logger?.warn('position.reconcile.confirmed_transition_expired', diagnostic);
  emit('position-entity:diagnostic', { kind: 'confirmed-transition-expired', ...diagnostic });
  if (process.env.NODE_ENV !== 'production') {
    _bumpPerf('positionEntity.confirmedTransitionExpirations');
  }
}

function observedState(observed: Readonly<Record<string, unknown>> | undefined): string {
  return typeof observed?.resultState === 'string' ? observed.resultState : 'missing';
}

function parseUnsigned(value: unknown): bigint | null {
  return typeof value === 'string' && CANONICAL_UNSIGNED_INTEGER.test(value) ? BigInt(value) : null;
}
